import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight } from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { FAQ } from "@/lib/testIds";
import { FAQ_CATEGORIES, FAQ_ITEMS } from "@/data/staticContent";

export const FaqPage = () => {
  const [category, setCategory] = useState("all");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    return FAQ_ITEMS.filter((f) => {
      if (category !== "all" && f.category !== category) return false;
      if (query) {
        const q = query.toLowerCase();
        return (
          f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [category, query]);

  return (
    <main data-testid={FAQ.root} className="pb-24">
      {/* Hero */}
      <section className="mx-auto max-w-[1400px] px-5 pt-20 md:px-10 md:pt-32">
        <p className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground md:text-xs">
          Help centre · Nº 04
        </p>
        <h1 className="mt-5 font-display text-5xl font-medium leading-[0.95] tracking-tight md:mt-8 md:text-[8rem]">
          Answers,
          <br />
          <span className="italic">before</span> you ask.
        </h1>

        {/* Search */}
        <div className="mt-10 flex max-w-2xl items-center gap-3 border-b border-foreground/20 pb-3 md:mt-14">
          <Search size={16} className="text-muted-foreground" />
          <input
            data-testid={FAQ.search}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search for a question — try 'cancel'…"
            className="flex-1 bg-transparent text-base text-foreground placeholder:text-muted-foreground focus:outline-none"
          />
        </div>

        {/* Categories */}
        <div className="mt-8 flex flex-wrap items-center gap-2 md:mt-10">
          <CategoryChip
            id="all"
            label="All"
            active={category === "all"}
            onClick={() => setCategory("all")}
          />
          {FAQ_CATEGORIES.map((c) => (
            <CategoryChip
              key={c.id}
              id={c.id}
              label={c.label}
              active={category === c.id}
              onClick={() => setCategory(c.id)}
            />
          ))}
        </div>
      </section>

      {/* Items */}
      <section className="mx-auto mt-12 max-w-[900px] px-5 md:mt-16 md:px-10">
        {filtered.length === 0 ? (
          <div
            data-testid={FAQ.empty}
            className="border border-dashed border-foreground/20 px-6 py-16 text-center md:py-24"
          >
            <p className="font-display text-3xl md:text-4xl">
              Nothing matches.
            </p>
            <p className="mt-3 text-sm text-muted-foreground md:text-base">
              Try a different word — or ask us directly.
            </p>
            <Link
              to="/contact-us"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-foreground px-6 py-3 text-sm text-background"
            >
              Contact support <ArrowRight size={16} />
            </Link>
          </div>
        ) : (
          <Accordion type="single" collapsible className="w-full">
            {filtered.map((f, i) => (
              <AccordionItem
                key={f.q}
                value={`faq-${i}`}
                data-testid={FAQ.item(i)}
                className="border-foreground/10"
              >
                <AccordionTrigger className="py-6 font-display text-lg font-medium tracking-tight text-foreground hover:no-underline md:text-xl">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="pr-6 text-base leading-relaxed text-muted-foreground">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </section>

      {/* CTA */}
      <section className="mx-auto mt-24 max-w-[1400px] px-5 md:mt-32 md:px-10">
        <div className="flex flex-col items-start justify-between gap-6 border-t border-foreground/10 pt-10 md:flex-row md:items-end">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground md:text-xs">
              Still stuck?
            </p>
            <h3 className="mt-3 font-display text-3xl font-medium leading-tight tracking-tight md:text-5xl">
              Human replies within
              <br />
              <span className="italic">a day</span>.
            </h3>
          </div>
          <Link
            to="/contact-us"
            className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground transition-transform duration-300 hover:-translate-y-0.5"
          >
            Message support <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </main>
  );
};

const CategoryChip = ({ id, label, active, onClick }) => (
  <button
    type="button"
    data-testid={FAQ.category(id)}
    onClick={onClick}
    className={`whitespace-nowrap rounded-full border px-4 py-2 text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
      active
        ? "border-foreground bg-foreground text-background"
        : "border-foreground/15 text-muted-foreground hover:text-foreground"
    }`}
  >
    {label}
  </button>
);

export default FaqPage;
